// CheckboxDropdown.jsx — Shared multi-select dropdown with checkboxes
// Used by: Market Watch (FilterBar + DataTable headers) and Positions (filter bar + column headers)
//
// Props:
//   label      — Display name (e.g., "Expiry", "Sector")
//   selected   — Array of selected values ([] = show all)
//   setSelected — Setter function for the array
//   options    — Array of values to show (['All', 'Banks', 'FMCG', ...]) — 'All' is auto-skipped
//   t          — Theme object from getTheme()
//   compact    — If true, shows a small filter icon instead of a labeled button (for column headers)

import React, { useState, useRef, useEffect } from 'react';

function CheckboxDropdown({ label, selected, setSelected, options, t, compact = false }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const isActive = selected.length > 0;

  const toggle = (value) => {
    if (selected.includes(value)) {
      setSelected(selected.filter(v => v !== value));
    } else {
      setSelected([...selected, value]);
    }
  };

  // options comes in as ['All', 'Banks', 'FMCG', ...] — skip 'All', we handle it via buttons
  const realOptions = options.filter(o => o !== 'All');

  const clearAll = () => setSelected([]);
  const selectAll = () => setSelected([...realOptions]);

  // ─── Compact mode: small filter icon for column headers ───
  if (compact) {
    return (
      <div ref={ref} style={{ position: 'relative', display: 'inline-block', marginLeft: 3 }}>
        <span
          onClick={e => { e.stopPropagation(); setOpen(!open); }}
          style={{
            cursor: 'pointer',
            fontSize: 9,
            color: isActive ? '#3b82f6' : (t.textDim || '#4a5568'),
            fontWeight: isActive ? 700 : 400,
            userSelect: 'none',
          }}
          title={`Filter ${label}`}
        >
          {isActive ? '▼' : '▽'}
        </span>

        {open && (
          <div
            onClick={e => e.stopPropagation()}
            style={{
              position: 'absolute',
              top: '100%',
              left: -60,
              marginTop: 4,
              background: t.bgCard || t.bg,
              border: `1px solid ${t.border}`,
              borderRadius: 6,
              boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
              zIndex: 9999,
              minWidth: 160,
              maxHeight: 280,
              overflowY: 'auto',
              padding: '4px 0',
            }}
          >
            {/* Select All / Clear row */}
            <div style={{
              padding: '4px 10px 6px',
              borderBottom: `1px solid ${t.border}`,
              display: 'flex',
              justifyContent: 'space-between',
              gap: 8,
            }}>
              <button onClick={selectAll} style={{ fontSize: 10, color: t.textMuted, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>
                Select all
              </button>
              <button onClick={clearAll} style={{ fontSize: 10, color: t.textMuted, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>
                Clear (show all)
              </button>
            </div>

            {realOptions.map(opt => {
              const checked = selected.includes(opt);
              return (
                <label
                  key={opt}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '4px 10px', fontSize: 11, color: t.text,
                    cursor: 'pointer', userSelect: 'none',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = t.hoverBg || 'rgba(128,128,128,0.1)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <input type="checkbox" checked={checked} onChange={() => toggle(opt)} style={{ margin: 0, cursor: 'pointer' }} />
                  {opt}
                </label>
              );
            })}

            {realOptions.length === 0 && (
              <div style={{ padding: '8px 10px', fontSize: 11, color: t.textMuted }}>No options</div>
            )}
          </div>
        )}
      </div>
    );
  }

  // ─── Normal mode: labeled button (for filter bars) ───
  const buttonText = selected.length === 0
    ? `All ${label}`
    : selected.length === 1
      ? selected[0]
      : `${label} (${selected.length})`;

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          padding: '5px 8px',
          fontSize: 11,
          background: isActive ? (t.filterActiveBg || 'rgba(59,130,246,0.15)') : t.bgInput,
          border: `1px solid ${isActive ? (t.filterActiveBorder || 'rgba(59,130,246,0.5)') : t.borderInput}`,
          borderRadius: 4,
          color: isActive ? (t.filterActiveText || '#3b82f6') : t.textMuted,
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          fontWeight: isActive ? 600 : 400,
        }}
      >
        {buttonText} ▾
      </button>

      {open && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          marginTop: 4,
          background: t.bgCard || t.bg,
          border: `1px solid ${t.border}`,
          borderRadius: 6,
          boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
          zIndex: 9999,
          minWidth: 160,
          maxHeight: 280,
          overflowY: 'auto',
          padding: '4px 0',
        }}>
          {/* Select All / Clear row */}
          <div style={{
            padding: '4px 10px 6px',
            borderBottom: `1px solid ${t.border}`,
            display: 'flex',
            justifyContent: 'space-between',
            gap: 8,
          }}>
            <button onClick={selectAll} style={{ fontSize: 10, color: t.textMuted, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>
              Select all
            </button>
            <button onClick={clearAll} style={{ fontSize: 10, color: t.textMuted, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>
              Clear (show all)
            </button>
          </div>

          {realOptions.map(opt => {
            const checked = selected.includes(opt);
            return (
              <label
                key={opt}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '4px 10px', fontSize: 11, color: t.text,
                  cursor: 'pointer', userSelect: 'none',
                }}
                onMouseEnter={e => e.currentTarget.style.background = t.hoverBg || 'rgba(128,128,128,0.1)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <input type="checkbox" checked={checked} onChange={() => toggle(opt)} style={{ margin: 0, cursor: 'pointer' }} />
                {opt}
              </label>
            );
          })}

          {realOptions.length === 0 && (
            <div style={{ padding: '8px 10px', fontSize: 11, color: t.textMuted }}>No options</div>
          )}
        </div>
      )}
    </div>
  );
}

export default CheckboxDropdown;
